import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  Paper,
  Tooltip,
} from '@mui/material';
import { FaAndroid, FaGooglePlay, FaCopy, FaCheck, FaExternalLinkAlt, FaGift } from 'react-icons/fa';
import { IoClose, IoSparkles } from 'react-icons/io5';

// Helper para detectar si el usuario navega desde un dispositivo Android
export const isAndroidDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = (navigator.userAgent || navigator.vendor || window.opera || '').toLowerCase();
  return ua.includes('android');
};

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.dside';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '92%', sm: 480 },
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'rgba(15, 20, 32, 0.96)',
  backdropFilter: 'blur(25px)',
  border: '1px solid rgba(61, 220, 132, 0.35)',
  boxShadow: '0 25px 70px rgba(0, 0, 0, 0.75), 0 0 40px rgba(61, 220, 132, 0.25)',
  borderRadius: 4,
  p: { xs: 2.5, sm: 3.5 },
  color: '#ffffff',
  outline: 'none',
  position: 'relative',
};

export default function AndroidBetaModal({ forceOpen = false, onCloseCustom }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Si viene forzado por prop
    if (forceOpen) {
      setOpen(true);
      return;
    }

    // Verificar si se acaba de iniciar sesión con Kick y el dispositivo es Android
    const shouldShow = localStorage.getItem('show_android_beta_modal') === 'true';
    const isAndroid = isAndroidDevice();

    if (shouldShow && isAndroid) {
      setOpen(true);
      // Limpiamos la bandera para que no se abra en cada navegación posterior
      localStorage.removeItem('show_android_beta_modal');
    }

    // Permitir abrir manualmente para pruebas vía console o eventos
    const handleTrigger = () => setOpen(true);
    window.addEventListener('open_android_beta_modal', handleTrigger);
    window.openAndroidBetaModal = handleTrigger;

    return () => {
      window.removeEventListener('open_android_beta_modal', handleTrigger);
      delete window.openAndroidBetaModal;
    };
  }, [forceOpen]);

  const handleClose = () => {
    setOpen(false);
    localStorage.removeItem('show_android_beta_modal');
    if (onCloseCustom) onCloseCustom();
  };

  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(PLAY_STORE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      aria-labelledby="android-beta-title"
      aria-describedby="android-beta-description"
    >
      <Box sx={modalStyle}>
        {/* Glow de fondo decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: -60,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 200,
            height: 120,
            background: 'linear-gradient(135deg, #3DDC84, #53FC18)',
            filter: 'blur(70px)',
            opacity: 0.25,
            pointerEvents: 'none',
          }}
        />

        {/* Botón Cerrar (X) */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 14,
            right: 14,
            color: '#9CA3AF',
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            '&:hover': {
              color: '#FFF',
              bgcolor: 'rgba(255, 255, 255, 0.12)',
            },
          }}
          aria-label="Cerrar"
        >
          <IoClose size={22} />
        </IconButton>

        {/* Header con Icono y Tag */}
        <Box sx={{ textAlign: 'center', pt: 1, pb: 2 }}>
          {/* Icono animado / destacado */}
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '24px',
              background: 'linear-gradient(135deg, rgba(61, 220, 132, 0.2) 0%, rgba(83, 252, 24, 0.1) 100%)',
              border: '2px solid rgba(61, 220, 132, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              boxShadow: '0 8px 25px rgba(61, 220, 132, 0.3)',
              position: 'relative',
            }}
          >
            <FaAndroid size={40} color="#3DDC84" />
            <Box
              sx={{
                position: 'absolute',
                top: -6,
                right: -6,
                background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                borderRadius: '50%',
                p: 0.6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(255, 215, 0, 0.5)',
              }}
            >
              <IoSparkles size={14} color="#000" />
            </Box>
          </Box>

          {/* Badge Beta Cerrada */}
          <Chip
            icon={<FaGift size={13} style={{ color: '#000', marginLeft: '6px' }} />}
            label="BETA CERRADA • ANDROID"
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #3DDC84 0%, #53FC18 100%)',
              color: '#0a0d14',
              fontWeight: 800,
              fontSize: '0.75rem',
              letterSpacing: '0.5px',
              px: 1,
              py: 0.5,
              mb: 1.5,
              boxShadow: '0 4px 12px rgba(61, 220, 132, 0.35)',
            }}
          />

          {/* Título Principal */}
          <Typography
            id="android-beta-title"
            variant="h5"
            sx={{
              fontWeight: 800,
              fontFamily: 'Outfit, sans-serif',
              color: '#FFFFFF',
              lineHeight: 1.25,
              mb: 1.5,
            }}
          >
            ¡Estás con suerte!
          </Typography>

          <Typography
            sx={{
              color: '#3DDC84',
              fontWeight: 700,
              fontSize: '1.05rem',
              fontFamily: 'Outfit, sans-serif',
              mb: 1.5,
            }}
          >
            Sos uno de los elegidos para probar la aplicación de la comunidad en la beta cerrada.
          </Typography>

          <Typography
            id="android-beta-description"
            sx={{
              color: '#9CA3AF',
              fontSize: '0.92rem',
              lineHeight: 1.6,
              fontFamily: 'Outfit, sans-serif',
              px: { xs: 0.5, sm: 1 },
            }}
          >
            Esto es solo para las personas que tienen <b>Android</b>, si querés formar parte, accedé al siguiente link y podés descargarla gratis:
          </Typography>
        </Box>

        {/* Caja de Link con botón de Copiar */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 3,
            bgcolor: 'rgba(0, 0, 0, 0.35)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 2.5,
          }}
        >
          <Typography
            sx={{
              color: '#D1D5DB',
              fontSize: '0.8rem',
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              pl: 0.5,
            }}
          >
            {PLAY_STORE_URL}
          </Typography>

          <Tooltip title={copied ? '¡Copiado!' : 'Copiar enlace'} arrow>
            <Button
              size="small"
              onClick={handleCopyLink}
              startIcon={copied ? <FaCheck size={13} /> : <FaCopy size={13} />}
              sx={{
                minWidth: 'auto',
                px: 1.8,
                py: 0.6,
                fontSize: '0.75rem',
                fontWeight: 700,
                color: copied ? '#53FC18' : '#FFFFFF',
                bgcolor: copied ? 'rgba(83, 252, 24, 0.15)' : 'rgba(255, 255, 255, 0.1)',
                border: copied ? '1px solid #53FC18' : '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: copied ? 'rgba(83, 252, 24, 0.25)' : 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
          </Tooltip>
        </Paper>

        {/* Acciones principales */}
        <Stack spacing={1.5}>
          {/* Botón Descargar en Google Play */}
          <Button
            component="a"
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClose}
            variant="contained"
            startIcon={<FaGooglePlay size={20} />}
            endIcon={<FaExternalLinkAlt size={14} />}
            sx={{
              height: 54,
              borderRadius: '16px',
              fontWeight: 800,
              fontSize: '1rem',
              textTransform: 'none',
              fontFamily: 'Outfit, sans-serif',
              color: '#0b0f19',
              background: 'linear-gradient(135deg, #3DDC84 0%, #53FC18 100%)',
              boxShadow: '0 10px 25px rgba(61, 220, 132, 0.4)',
              transition: 'all 0.25s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #53FC18 0%, #3DDC84 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 15px 32px rgba(61, 220, 132, 0.55)',
              },
            }}
          >
            Descargar Gratis en Play Store
          </Button>

          {/* Botón de descartar / tal vez luego */}
          <Button
            onClick={handleClose}
            sx={{
              color: '#9CA3AF',
              fontSize: '0.88rem',
              fontWeight: 600,
              textTransform: 'none',
              fontFamily: 'Outfit, sans-serif',
              py: 0.8,
              '&:hover': {
                color: '#FFF',
                bgcolor: 'transparent',
              },
            }}
          >
            Quizás más tarde
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}
